/**
 * Platform Referral Management
 *
 * Admin panel for managing platform referrers (merchants & marketers),
 * viewing referrals, and approving/canceling rewards.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Users, Gift, DollarSign, TrendingUp, Search, Filter,
  RefreshCw, ChevronLeft, ChevronRight, Eye, Ban, Flag,
  CheckCircle, XCircle, Clock, AlertTriangle, Settings,
  Copy, ExternalLink, MoreVertical, MessageCircle, Send
} from 'lucide-react';
import './ReferralManagement.css';

// Format currency
const formatCurrency = (amount) => {
  const value = Number(amount) || 0;
  return new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: 'NGN',
    minimumFractionDigits: 0
  }).format(value);
};

// Format date
const formatDate = (dateString) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('en-NG', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

// Status badge component
const StatusBadge = ({ status, type = 'default' }) => {
  const statusColors = {
    ACTIVE: 'prm-badge-success',
    SUSPENDED: 'prm-badge-danger',
    PAUSED: 'prm-badge-warning',
    DEACTIVATED: 'prm-badge-muted',
    PENDING: 'prm-badge-warning',
    VERIFIED: 'prm-badge-info',
    UPGRADED: 'prm-badge-success',
    CHURNED: 'prm-badge-danger',
    APPROVED: 'prm-badge-success',
    PAID: 'prm-badge-success',
    CANCELLED: 'prm-badge-danger',
    EXPIRED: 'prm-badge-muted'
  };
  return (
    <span className={`prm-badge ${statusColors[status] || 'prm-badge-muted'}`}>
      {status}
    </span>
  );
};

// Toast component
const Toast = ({ type, message, onClose }) => (
  <div className={`prm-toast prm-toast-${type}`}>
    {type === 'success' && <CheckCircle size={18} />}
    {type === 'error' && <XCircle size={18} />}
    {type === 'warning' && <AlertTriangle size={18} />}
    <span>{message}</span>
    <button onClick={onClose} className="prm-toast-close">&times;</button>
  </div>
);

// Confirmation Modal
const ConfirmModal = ({ isOpen, title, message, onConfirm, onCancel, confirmText = 'Confirm', danger = false }) => {
  if (!isOpen) return null;
  return (
    <div className="prm-modal-overlay" onClick={onCancel}>
      <div className="prm-modal" onClick={e => e.stopPropagation()}>
        <h3 className="prm-modal-title">{title}</h3>
        <p className="prm-modal-message">{message}</p>
        <div className="prm-modal-actions">
          <button className="prm-btn prm-btn-secondary" onClick={onCancel}>Cancel</button>
          <button
            className={`prm-btn ${danger ? 'prm-btn-danger' : 'prm-btn-primary'}`}
            onClick={onConfirm}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

// Reason Modal (for suspend/flag/cancel actions)
const ReasonModal = ({ isOpen, title, onSubmit, onCancel, placeholder = 'Enter reason...' }) => {
  const [reason, setReason] = useState('');

  if (!isOpen) return null;

  const handleSubmit = () => {
    if (reason.trim()) {
      onSubmit(reason);
      setReason('');
    }
  };

  return (
    <div className="prm-modal-overlay" onClick={onCancel}>
      <div className="prm-modal" onClick={e => e.stopPropagation()}>
        <h3 className="prm-modal-title">{title}</h3>
        <textarea
          className="prm-modal-textarea"
          value={reason}
          onChange={e => setReason(e.target.value)}
          placeholder={placeholder}
          rows={3}
        />
        <div className="prm-modal-actions">
          <button className="prm-btn prm-btn-secondary" onClick={onCancel}>Cancel</button>
          <button
            className="prm-btn prm-btn-primary"
            onClick={handleSubmit}
            disabled={!reason.trim()}
          >
            Submit
          </button>
        </div>
      </div>
    </div>
  );
};

export default function ReferralManagement() {
  // Tab state
  const [activeTab, setActiveTab] = useState('overview');

  // Data state
  const [overview, setOverview] = useState(null);
  const [referrers, setReferrers] = useState([]);
  const [rewards, setRewards] = useState([]);
  const [config, setConfig] = useState(null);
  const [tiers, setTiers] = useState([]);
  const [selectedReferrer, setSelectedReferrer] = useState(null);

  // Filter state
  const [referrerType, setReferrerType] = useState('all');
  const [referrerStatus, setReferrerStatus] = useState('all');
  const [rewardStatus, setRewardStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  // Pagination
  const [referrersPage, setReferrersPage] = useState(1);
  const [referrersPagination, setReferrersPagination] = useState({ total: 0, pages: 1 });
  const [rewardsPage, setRewardsPage] = useState(1);
  const [rewardsPagination, setRewardsPagination] = useState({ total: 0, pages: 1 });

  // Payouts
  const [payouts, setPayouts] = useState([]);
  const [payoutsPage, setPayoutsPage] = useState(1);
  const [payoutsPagination, setPayoutsPagination] = useState({ total: 0, pages: 1 });
  const [payoutStatus, setPayoutStatus] = useState('PENDING');
  const [selectedPayout, setSelectedPayout] = useState(null);
  const [payoutPaymentRef, setPayoutPaymentRef] = useState('');
  const [payoutAdminNotes, setPayoutAdminNotes] = useState('');

  // UI state
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [confirmModal, setConfirmModal] = useState({ isOpen: false });
  const [reasonModal, setReasonModal] = useState({ isOpen: false });
  const [selectedRewards, setSelectedRewards] = useState([]);
  // Tracks which row's "Message" dropdown is currently open.
  const [messageMenu, setMessageMenu] = useState({ openForId: null });

  // API helper
  const apiFetch = useCallback(async (url, options = {}) => {
    const res = await fetch(url, {
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', ...options.headers },
      ...options,
    });
    if (!res.ok) {
      const errData = await res.json().catch(() => ({}));
      throw new Error(errData.error || errData.message || `Request failed (${res.status})`);
    }
    return res.json();
  }, []);

  // Toast helper
  const showToast = useCallback((type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 4000);
  }, []);

  // Fetch overview stats
  const fetchOverview = useCallback(async () => {
    try {
      const data = await apiFetch('/api/referral/admin/overview');
      setOverview(data);
    } catch (err) {
      console.error('Error fetching overview:', err);
      showToast('error', 'Failed to load overview');
    }
  }, [apiFetch, showToast]);

  // Fetch referrers list
  const fetchReferrers = useCallback(async (page = 1) => {
    try {
      const params = new URLSearchParams({ page: String(page), limit: '20' });
      if (referrerType !== 'all') params.set('type', referrerType);
      if (referrerStatus !== 'all') params.set('status', referrerStatus);
      if (searchTerm) params.set('search', searchTerm);

      const data = await apiFetch(`/api/referral/admin/referrers?${params}`);
      setReferrers(data.referrers || []);
      setReferrersPagination({ total: data.total || 0, pages: data.pages || 1 });
    } catch (err) {
      console.error('Error fetching referrers:', err);
      showToast('error', 'Failed to load referrers');
    }
  }, [apiFetch, referrerType, referrerStatus, searchTerm, showToast]);

  // Fetch rewards list
  const fetchRewards = useCallback(async (page = 1) => {
    try {
      const params = new URLSearchParams({ page: String(page), limit: '20' });
      if (rewardStatus !== 'all') params.set('status', rewardStatus);

      const data = await apiFetch(`/api/referral/admin/rewards?${params}`);
      setRewards(data.rewards || []);
      setRewardsPagination({ total: data.total || 0, pages: data.pages || 1 });
    } catch (err) {
      console.error('Error fetching rewards:', err);
      showToast('error', 'Failed to load rewards');
    }
  }, [apiFetch, rewardStatus, showToast]);

  // Fetch config
  const fetchConfig = useCallback(async () => {
    try {
      const data = await apiFetch('/api/referral/admin/config');
      setConfig(data.config);
    } catch (err) {
      console.error('Error fetching config:', err);
      showToast('error', 'Failed to load config');
    }
  }, [apiFetch, showToast]);

  // Fetch tiers (includes inactive for admin)
  const fetchTiers = useCallback(async () => {
    try {
      // Use referral config endpoint with includeInactive to get all tiers
      const data = await apiFetch('/api/referral/config?includeInactive=true');
      const tierList = data.tiers || [];
      setTiers(tierList.map(t => ({ id: t.id, name: t.name, isActive: t.isActive })));
    } catch (err) {
      console.error('Error fetching tiers:', err);
      // Fallback to default tiers if fetch fails
      setTiers([
        { id: 'FREE', name: 'Free', isActive: true },
        { id: 'PRO', name: 'Pro', isActive: true },
        { id: 'PREMIUM', name: 'Premium', isActive: true },
        { id: 'PRO PLUS', name: 'Pro Plus', isActive: true },
        { id: 'ENTERPRISE', name: 'Enterprise', isActive: true }
      ]);
    }
  }, [apiFetch]);

  // Fetch referrer details
  const fetchReferrerDetails = useCallback(async (id) => {
    try {
      const data = await apiFetch(`/api/referral/admin/referrers/${id}`);
      // API returns { referrer, referrals, rewards, stats }
      setSelectedReferrer({
        ...data.referrer,
        referrals: data.referrals || [],
        rewards: data.rewards || [],
        stats: data.stats || {}
      });
    } catch (err) {
      console.error('Error fetching referrer:', err);
      showToast('error', 'Failed to load referrer details');
    }
  }, [apiFetch, showToast]);

  // Initial load
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchOverview(), fetchConfig(), fetchTiers()]);
      setLoading(false);
    };
    loadData();
  }, [fetchOverview, fetchConfig, fetchTiers]);

  // Fetch payouts
  const fetchPayouts = useCallback(async (page = 1) => {
    try {
      const params = new URLSearchParams({ page: String(page), limit: '20' });
      if (payoutStatus !== 'all') params.set('status', payoutStatus);
      const data = await apiFetch(`/api/referral/admin/payouts?${params}`);
      setPayouts(data.payouts || []);
      setPayoutsPagination({ total: data.total || 0, pages: data.pages || 1 });
    } catch (err) {
      console.error('Error fetching payouts:', err);
      showToast('error', 'Failed to load payouts');
    }
  }, [apiFetch, payoutStatus, showToast]);

  // Load tab-specific data
  useEffect(() => {
    if (activeTab === 'referrers') {
      fetchReferrers(referrersPage);
    } else if (activeTab === 'rewards') {
      fetchRewards(rewardsPage);
    } else if (activeTab === 'payouts') {
      fetchPayouts(payoutsPage);
    }
  }, [activeTab, referrersPage, rewardsPage, payoutsPage, fetchReferrers, fetchRewards, fetchPayouts]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (activeTab === 'referrers') {
        setReferrersPage(1);
        fetchReferrers(1);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Suspend/unsuspend referrer
  const handleSuspend = async (referrerId, suspend, notes = '') => {
    setActionLoading(true);
    try {
      await apiFetch(`/api/referral/admin/referrers/${referrerId}/suspend`, {
        method: 'POST',
        body: JSON.stringify({ suspend, notes })
      });
      showToast('success', suspend ? 'Referrer suspended' : 'Referrer activated');
      fetchReferrers(referrersPage);
      if (selectedReferrer?.id === referrerId) {
        fetchReferrerDetails(referrerId);
      }
    } catch (err) {
      showToast('error', err.message);
    } finally {
      setActionLoading(false);
      setReasonModal({ isOpen: false });
    }
  };

  // Flag referrer
  const handleFlag = async (referrerId, reason) => {
    setActionLoading(true);
    try {
      await apiFetch(`/api/referral/admin/referrers/${referrerId}/flag`, {
        method: 'POST',
        body: JSON.stringify({ reason })
      });
      showToast('success', 'Referrer flagged');
      fetchReferrers(referrersPage);
    } catch (err) {
      showToast('error', err.message);
    } finally {
      setActionLoading(false);
      setReasonModal({ isOpen: false });
    }
  };

  // Clear a referrer flag (admin)
  const handleUnflag = async (referrerId) => {
    setActionLoading(true);
    try {
      await apiFetch(`/api/referral/admin/referrers/${referrerId}/unflag`, {
        method: 'POST'
      });
      showToast('success', 'Flag cleared');
      fetchReferrers(referrersPage);
      if (selectedReferrer?.id === referrerId) {
        fetchReferrerDetails(referrerId);
      }
    } catch (err) {
      showToast('error', err.message);
    } finally {
      setActionLoading(false);
    }
  };

  // Open WhatsApp/SMS with prefilled message addressed to the referrer.
  // Click-to-chat — no provider integration needed; uses the admin's own
  // device WhatsApp/Messages app. `channel` is 'whatsapp' or 'sms'.
  const openClickToMessage = (referrer, channel) => {
    const phone = (referrer?.phone || '').replace(/[^\d+]/g, '');
    if (!phone) {
      showToast('error', 'No phone number on file for this referrer');
      return;
    }
    const firstName = (referrer?.name || '').split(' ')[0] || 'there';
    const text = `Hi ${firstName}, this is the Revstar team — `;
    if (channel === 'whatsapp') {
      // wa.me requires the leading + to be stripped
      const waPhone = phone.replace(/^\+/, '');
      window.open(`https://wa.me/${waPhone}?text=${encodeURIComponent(text)}`, '_blank', 'noopener,noreferrer');
    } else {
      // sms: scheme works on mobile and most desktops with a default Messages app
      window.location.href = `sms:${phone}?body=${encodeURIComponent(text)}`;
    }
    setMessageMenu({ openForId: null });
  };

  // Approve rewards
  const handleApproveRewards = async () => {
    if (selectedRewards.length === 0) return;
    setActionLoading(true);
    try {
      await apiFetch('/api/referral/admin/rewards/approve', {
        method: 'POST',
        body: JSON.stringify({ rewardIds: selectedRewards })
      });
      showToast('success', `${selectedRewards.length} reward(s) approved`);
      setSelectedRewards([]);
      fetchRewards(rewardsPage);
      fetchOverview();
    } catch (err) {
      showToast('error', err.message);
    } finally {
      setActionLoading(false);
    }
  };

  // Cancel rewards
  const handleCancelRewards = async (reason) => {
    if (selectedRewards.length === 0) return;
    setActionLoading(true);
    try {
      await apiFetch('/api/referral/admin/rewards/cancel', {
        method: 'POST',
        body: JSON.stringify({ rewardIds: selectedRewards, reason })
      });
      showToast('success', `${selectedRewards.length} reward(s) cancelled`);
      setSelectedRewards([]);
      fetchRewards(rewardsPage);
      fetchOverview();
    } catch (err) {
      showToast('error', err.message);
    } finally {
      setActionLoading(false);
      setReasonModal({ isOpen: false });
    }
  };

  // Process eligible rewards
  const handleProcessRewards = async () => {
    setActionLoading(true);
    try {
      const data = await apiFetch('/api/referral/admin/process-rewards', {
        method: 'POST'
      });
      showToast('success', `Processed: ${data.processed} rewards moved to eligible`);
      fetchRewards(rewardsPage);
      fetchOverview();
    } catch (err) {
      showToast('error', err.message);
    } finally {
      setActionLoading(false);
    }
  };

  // Payout: mark as processing
  const handlePayoutProcessing = async (payoutId) => {
    setActionLoading(true);
    try {
      await apiFetch(`/api/referral/admin/payouts/${payoutId}/processing`, { method: 'POST' });
      showToast('success', 'Marked as processing');
      fetchPayouts(payoutsPage);
      setSelectedPayout(prev => prev && prev.id === payoutId ? { ...prev, status: 'PROCESSING' } : prev);
    } catch (err) {
      showToast('error', err.message);
    } finally {
      setActionLoading(false);
    }
  };

  // Payout: mark as paid (requires payment reference)
  const handlePayoutPaid = async (payoutId) => {
    if (!payoutPaymentRef.trim()) {
      showToast('error', 'Enter a payment reference first');
      return;
    }
    setActionLoading(true);
    try {
      await apiFetch(`/api/referral/admin/payouts/${payoutId}/paid`, {
        method: 'POST',
        body: JSON.stringify({
          paymentReference: payoutPaymentRef.trim(),
          adminNotes: payoutAdminNotes.trim() || undefined
        })
      });
      showToast('success', 'Payout marked as paid');
      setPayoutPaymentRef('');
      setPayoutAdminNotes('');
      setSelectedPayout(null);
      fetchPayouts(payoutsPage);
      fetchOverview();
    } catch (err) {
      showToast('error', err.message);
    } finally {
      setActionLoading(false);
    }
  };

  // Payout: reject (refunds wallet)
  const handlePayoutReject = (payoutId) => {
    setReasonModal({
      isOpen: true,
      title: 'Reject Payout',
      placeholder: 'Reason for rejection (referrer will see this)...',
      onSubmit: async (reason) => {
        setActionLoading(true);
        try {
          await apiFetch(`/api/referral/admin/payouts/${payoutId}/reject`, {
            method: 'POST',
            body: JSON.stringify({ reason })
          });
          showToast('success', 'Payout rejected and funds returned');
          setSelectedPayout(null);
          fetchPayouts(payoutsPage);
          fetchOverview();
        } catch (err) {
          showToast('error', err.message);
        } finally {
          setActionLoading(false);
          setReasonModal({ isOpen: false });
        }
      }
    });
  };

  // Save config
  const handleSaveConfig = async () => {
    setActionLoading(true);
    try {
      await apiFetch('/api/referral/admin/config', {
        method: 'PUT',
        body: JSON.stringify(config)
      });
      showToast('success', 'Configuration saved');
    } catch (err) {
      showToast('error', err.message);
    } finally {
      setActionLoading(false);
    }
  };

  // Toggle reward selection
  const toggleRewardSelection = (rewardId) => {
    setSelectedRewards(prev =>
      prev.includes(rewardId)
        ? prev.filter(id => id !== rewardId)
        : [...prev, rewardId]
    );
  };

  // Select all pending rewards
  const selectAllPending = () => {
    const pendingIds = rewards.filter(r => r.status === 'PENDING').map(r => r.id);
    setSelectedRewards(pendingIds);
  };

  // Copy to clipboard
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    showToast('success', 'Copied to clipboard');
  };

  // Render overview tab
  const renderOverview = () => (
    <div className="prm-overview">
      {/* Stats Grid */}
      <div className="prm-stats-grid">
        <div className="prm-stat-card">
          <div className="prm-stat-icon prm-stat-icon-blue">
            <Users size={24} />
          </div>
          <div className="prm-stat-content">
            <span className="prm-stat-value">{overview?.referrers?.total || 0}</span>
            <span className="prm-stat-label">Total Referrers</span>
          </div>
        </div>
        <div className="prm-stat-card">
          <div className="prm-stat-icon prm-stat-icon-green">
            <Gift size={24} />
          </div>
          <div className="prm-stat-content">
            <span className="prm-stat-value">{overview?.referrals?.total || 0}</span>
            <span className="prm-stat-label">Total Referrals</span>
          </div>
        </div>
        <div className="prm-stat-card">
          <div className="prm-stat-icon prm-stat-icon-purple">
            <TrendingUp size={24} />
          </div>
          <div className="prm-stat-content">
            <span className="prm-stat-value">{overview?.referrals?.verified || 0}</span>
            <span className="prm-stat-label">Verified</span>
          </div>
        </div>
        <div className="prm-stat-card">
          <div className="prm-stat-icon prm-stat-icon-orange">
            <DollarSign size={24} />
          </div>
          <div className="prm-stat-content">
            <span className="prm-stat-value">{formatCurrency((overview?.rewards?.pending || 0) + (overview?.rewards?.paid || 0))}</span>
            <span className="prm-stat-label">Total Rewards</span>
          </div>
        </div>
      </div>

      {/* Breakdown Cards */}
      <div className="prm-breakdown-grid">
        <div className="prm-breakdown-card">
          <h3 className="prm-breakdown-title">Referrer Types</h3>
          <div className="prm-breakdown-items">
            <div className="prm-breakdown-item">
              <span>Merchants</span>
              <span className="prm-breakdown-value">{overview?.referrers?.merchants || 0}</span>
            </div>
            <div className="prm-breakdown-item">
              <span>Marketers</span>
              <span className="prm-breakdown-value">{overview?.referrers?.marketers || 0}</span>
            </div>
          </div>
        </div>
        <div className="prm-breakdown-card">
          <h3 className="prm-breakdown-title">Rewards Status</h3>
          <div className="prm-breakdown-items">
            <div className="prm-breakdown-item">
              <span>Pending</span>
              <span className="prm-breakdown-value prm-text-warning">{formatCurrency(overview?.rewards?.pending || 0)}</span>
            </div>
            <div className="prm-breakdown-item">
              <span>Paid</span>
              <span className="prm-breakdown-value prm-text-info">{formatCurrency(overview?.rewards?.paid || 0)}</span>
            </div>
          </div>
        </div>
        <div className="prm-breakdown-card">
          <h3 className="prm-breakdown-title">Quick Actions</h3>
          <div className="prm-quick-actions">
            <button
              className="prm-btn prm-btn-secondary"
              onClick={handleProcessRewards}
              disabled={actionLoading}
            >
              <Clock size={16} />
              Process Eligible Rewards
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // Render referrers tab
  const renderReferrers = () => (
    <div className="prm-referrers">
      {/* Filters */}
      <div className="prm-filters">
        <div className="prm-search-box">
          <Search size={18} />
          <input
            type="text"
            placeholder="Search by name, email, or code..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </div>
        <select
          className="prm-select"
          value={referrerType}
          onChange={e => { setReferrerType(e.target.value); setReferrersPage(1); }}
        >
          <option value="all">All Types</option>
          <option value="MERCHANT">Merchants</option>
          <option value="MARKETER">Marketers</option>
        </select>
        <select
          className="prm-select"
          value={referrerStatus}
          onChange={e => { setReferrerStatus(e.target.value); setReferrersPage(1); }}
        >
          <option value="all">All Status</option>
          <option value="ACTIVE">Active</option>
          <option value="SUSPENDED">Suspended</option>
          <option value="PAUSED">Paused</option>
        </select>
      </div>

      {/* Referrers Table */}
      <div className="prm-table-container">
        <table className="prm-table">
          <thead>
            <tr>
              <th>Referrer</th>
              <th>Type</th>
              <th>Code</th>
              <th>Referrals</th>
              <th>Verified</th>
              <th>Paid Signups</th>
              <th>Earnings</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {referrers.length === 0 ? (
              <tr>
                <td colSpan={9} className="prm-empty">No referrers found</td>
              </tr>
            ) : (
              referrers.map(referrer => (
                <tr key={referrer.id}>
                  <td>
                    <div className="prm-referrer-info">
                      <span className="prm-referrer-name">{referrer.name || referrer.merchant?.businessName || '-'}</span>
                      <span className="prm-referrer-email">{referrer.email || referrer.merchant?.email || '-'}</span>
                    </div>
                  </td>
                  <td>
                    <span className={`prm-type-badge prm-type-${referrer.type.toLowerCase()}`}>
                      {referrer.type}
                    </span>
                  </td>
                  <td>
                    <div className="prm-code-cell">
                      <code>{referrer.referralCode}</code>
                      <button
                        className="prm-icon-btn"
                        onClick={() => copyToClipboard(referrer.referralCode)}
                        title="Copy code"
                      >
                        <Copy size={14} />
                      </button>
                    </div>
                  </td>
                  <td>{referrer.totalReferrals || 0}</td>
                  <td>{referrer.verifiedReferrals || 0}</td>
                  <td>{referrer.paidReferrals || 0}</td>
                  <td>{formatCurrency(referrer.totalRewardsEarned || 0)}</td>
                  <td><StatusBadge status={referrer.status} /></td>
                  <td>
                    <div className="prm-actions">
                      <button
                        className="prm-icon-btn"
                        onClick={() => fetchReferrerDetails(referrer.id)}
                        title="View details"
                      >
                        <Eye size={16} />
                      </button>
                      {referrer.status === 'ACTIVE' ? (
                        <button
                          className="prm-icon-btn prm-icon-btn-danger"
                          onClick={() => setReasonModal({
                            isOpen: true,
                            title: 'Suspend Referrer',
                            onSubmit: (notes) => handleSuspend(referrer.id, true, notes),
                            placeholder: 'Reason for suspension...'
                          })}
                          title="Suspend"
                          disabled={actionLoading}
                        >
                          <Ban size={16} />
                        </button>
                      ) : (
                        <button
                          className="prm-icon-btn prm-icon-btn-success"
                          onClick={() => handleSuspend(referrer.id, false)}
                          title="Activate"
                          disabled={actionLoading}
                        >
                          <CheckCircle size={16} />
                        </button>
                      )}
                      {!referrer.isFlagged ? (
                        <button
                          className="prm-icon-btn prm-icon-btn-warning"
                          onClick={() => setReasonModal({
                            isOpen: true,
                            title: 'Flag Referrer',
                            onSubmit: (reason) => handleFlag(referrer.id, reason),
                            placeholder: 'Reason for flagging...'
                          })}
                          title="Flag"
                          disabled={actionLoading}
                        >
                          <Flag size={16} />
                        </button>
                      ) : (
                        <button
                          className="prm-icon-btn prm-icon-btn-success"
                          onClick={() => handleUnflag(referrer.id)}
                          title="Clear flag"
                          disabled={actionLoading}
                        >
                          <CheckCircle size={16} />
                        </button>
                      )}
                      {/* Message dropdown — click to send a WhatsApp or SMS
                          via the admin's own device (no provider needed). */}
                      <div className="prm-msg-wrap">
                        <button
                          className="prm-icon-btn"
                          onClick={() => setMessageMenu({
                            openForId: messageMenu.openForId === referrer.id ? null : referrer.id
                          })}
                          title={referrer.phone ? 'Message referrer' : 'No phone on file'}
                          disabled={!referrer.phone}
                        >
                          <MessageCircle size={16} />
                        </button>
                        {messageMenu.openForId === referrer.id && (
                          <div className="prm-msg-menu" role="menu">
                            <button
                              role="menuitem"
                              className="prm-msg-menu-item"
                              onClick={() => openClickToMessage(referrer, 'whatsapp')}
                            >
                              <Send size={14} /> WhatsApp
                            </button>
                            <button
                              role="menuitem"
                              className="prm-msg-menu-item"
                              onClick={() => openClickToMessage(referrer, 'sms')}
                            >
                              <Send size={14} /> SMS
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {referrersPagination.pages > 1 && (
        <div className="prm-pagination">
          <button
            className="prm-btn prm-btn-secondary"
            onClick={() => setReferrersPage(p => Math.max(1, p - 1))}
            disabled={referrersPage === 1}
          >
            <ChevronLeft size={16} />
          </button>
          <span className="prm-pagination-info">
            Page {referrersPage} of {referrersPagination.pages}
          </span>
          <button
            className="prm-btn prm-btn-secondary"
            onClick={() => setReferrersPage(p => Math.min(referrersPagination.pages, p + 1))}
            disabled={referrersPage === referrersPagination.pages}
          >
            <ChevronRight size={16} />
          </button>
        </div>
      )}

      {/* Referrer Detail Sidebar */}
      {selectedReferrer && (
        <div className="prm-detail-overlay" onClick={() => setSelectedReferrer(null)}>
          <div className="prm-detail-sidebar" onClick={e => e.stopPropagation()}>
            <div className="prm-detail-header">
              <h3>Referrer Details</h3>
              <button className="prm-icon-btn" onClick={() => setSelectedReferrer(null)}>
                <XCircle size={20} />
              </button>
            </div>
            <div className="prm-detail-content">
              <div className="prm-detail-section">
                <h4>Basic Info</h4>
                <div className="prm-detail-row">
                  <span>Name:</span>
                  <span>{selectedReferrer.name || selectedReferrer.merchant?.businessName || '-'}</span>
                </div>
                <div className="prm-detail-row">
                  <span>Email:</span>
                  <span>{selectedReferrer.email || selectedReferrer.merchant?.email || '-'}</span>
                </div>
                <div className="prm-detail-row">
                  <span>Type:</span>
                  <span className={`prm-type-badge prm-type-${selectedReferrer.type?.toLowerCase()}`}>
                    {selectedReferrer.type}
                  </span>
                </div>
                <div className="prm-detail-row">
                  <span>Code:</span>
                  <code>{selectedReferrer.referralCode}</code>
                </div>
                <div className="prm-detail-row">
                  <span>Status:</span>
                  <StatusBadge status={selectedReferrer.status} />
                </div>
                <div className="prm-detail-row">
                  <span>Joined:</span>
                  <span>{formatDate(selectedReferrer.createdAt)}</span>
                </div>
              </div>

              <div className="prm-detail-section">
                <h4>Stats</h4>
                <div className="prm-detail-row">
                  <span>Total Referrals:</span>
                  <span>{selectedReferrer.totalReferrals || 0}</span>
                </div>
                <div className="prm-detail-row">
                  <span>Verified:</span>
                  <span>{selectedReferrer.verifiedReferrals || 0}</span>
                </div>
                <div className="prm-detail-row">
                  <span>Paid Signups:</span>
                  <span>{selectedReferrer.paidReferrals || 0}</span>
                </div>
                <div className="prm-detail-row">
                  <span>Total Earned:</span>
                  <span>{formatCurrency(selectedReferrer.totalRewardsEarned || 0)}</span>
                </div>
                <div className="prm-detail-row">
                  <span>Pending:</span>
                  <span>{formatCurrency(selectedReferrer.pendingRewards || 0)}</span>
                </div>
                <div className="prm-detail-row">
                  <span>Paid Out:</span>
                  <span>{formatCurrency(selectedReferrer.paidRewards || 0)}</span>
                </div>
              </div>

              <div className="prm-detail-section">
                <h4>Wallet & Payout</h4>
                <div className="prm-detail-row">
                  <span>Wallet Balance:</span>
                  <span className="prm-text-success">{formatCurrency(selectedReferrer.walletBalance || 0)}</span>
                </div>
                <div className="prm-detail-row">
                  <span>Minimum Payout:</span>
                  <span>{formatCurrency(selectedReferrer.minimumPayout || 2000)}</span>
                </div>
                <div className="prm-detail-row">
                  <span>Payout Schedule:</span>
                  <span>{selectedReferrer.payoutSchedule || 'MANUAL'}</span>
                </div>
                {selectedReferrer.lastPayoutAt && (
                  <div className="prm-detail-row">
                    <span>Last Payout:</span>
                    <span>{formatDate(selectedReferrer.lastPayoutAt)} ({formatCurrency(selectedReferrer.lastPayoutAmount || 0)})</span>
                  </div>
                )}
              </div>

              {selectedReferrer.bankName && (
                <div className="prm-detail-section">
                  <h4>Payout Info</h4>
                  <div className="prm-detail-row">
                    <span>Bank:</span>
                    <span>{selectedReferrer.bankName}</span>
                  </div>
                  <div className="prm-detail-row">
                    <span>Account:</span>
                    <span>{selectedReferrer.accountNumber}</span>
                  </div>
                  <div className="prm-detail-row">
                    <span>Name:</span>
                    <span>{selectedReferrer.accountName}</span>
                  </div>
                </div>
              )}

              {selectedReferrer.referrals?.length > 0 && (
                <div className="prm-detail-section">
                  <h4>Referrals ({selectedReferrer.referrals.length})</h4>
                  <div className="prm-referrals-list">
                    {selectedReferrer.referrals.map(ref => (
                      <div key={ref.id} className="prm-referral-item">
                        <div className="prm-referral-header">
                          <span className="prm-referral-name">{ref.referredName || '-'}</span>
                          <StatusBadge status={ref.status} />
                        </div>
                        {ref.referredEmail && (
                          <div className="prm-referral-email">{ref.referredEmail}</div>
                        )}
                        <div className="prm-referral-details">
                          <span className="prm-referral-tier">
                            <strong>Tier:</strong> {ref.currentTier || 'FREE'}
                          </span>
                          <span className="prm-referral-date">
                            <strong>Referred:</strong> {formatDate(ref.signedUpAt)}
                          </span>
                        </div>
                        <div className="prm-referral-rewards">
                          {ref.rewardEarned > 0 && (
                            <span className="prm-reward-earned">
                              Earned: {formatCurrency(ref.rewardEarned)}
                            </span>
                          )}
                          {ref.pendingReward > 0 && (
                            <span className="prm-reward-pending">
                              Pending: {formatCurrency(ref.pendingReward)}
                            </span>
                          )}
                          {ref.rewardEarned === 0 && ref.pendingReward === 0 && ref.status !== 'UPGRADED' && (
                            <span className="prm-reward-none">No reward yet</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // Render rewards tab
  const renderRewards = () => (
    <div className="prm-rewards">
      {/* Filters & Actions */}
      <div className="prm-filters">
        <select
          className="prm-select"
          value={rewardStatus}
          onChange={e => { setRewardStatus(e.target.value); setRewardsPage(1); }}
        >
          <option value="all">All Status</option>
          <option value="PENDING">Pending</option>
          <option value="APPROVED">Approved</option>
          <option value="PAID">Paid</option>
          <option value="CANCELLED">Cancelled</option>
        </select>
        <div className="prm-filter-actions">
          {selectedRewards.length > 0 && (
            <>
              <button
                className="prm-btn prm-btn-success"
                onClick={handleApproveRewards}
                disabled={actionLoading}
              >
                <CheckCircle size={16} />
                Approve ({selectedRewards.length})
              </button>
              <button
                className="prm-btn prm-btn-danger"
                onClick={() => setReasonModal({
                  isOpen: true,
                  title: 'Cancel Rewards',
                  onSubmit: handleCancelRewards,
                  placeholder: 'Reason for cancellation...'
                })}
                disabled={actionLoading}
              >
                <XCircle size={16} />
                Cancel ({selectedRewards.length})
              </button>
            </>
          )}
          <button
            className="prm-btn prm-btn-secondary"
            onClick={selectAllPending}
          >
            Select All Pending
          </button>
        </div>
      </div>

      {/* Rewards Table */}
      <div className="prm-table-container">
        <table className="prm-table">
          <thead>
            <tr>
              <th>
                <input
                  type="checkbox"
                  checked={selectedRewards.length > 0 && selectedRewards.length === rewards.filter(r => r.status === 'PENDING').length}
                  onChange={() => {
                    if (selectedRewards.length > 0) {
                      setSelectedRewards([]);
                    } else {
                      selectAllPending();
                    }
                  }}
                />
              </th>
              <th>Referrer</th>
              <th>Type</th>
              <th>Event</th>
              <th>Amount</th>
              <th>Status</th>
              <th>Eligible At</th>
              <th>Created</th>
            </tr>
          </thead>
          <tbody>
            {rewards.length === 0 ? (
              <tr>
                <td colSpan={8} className="prm-empty">No rewards found</td>
              </tr>
            ) : (
              rewards.map(reward => (
                <tr key={reward.id} className={selectedRewards.includes(reward.id) ? 'prm-row-selected' : ''}>
                  <td>
                    {reward.status === 'PENDING' && (
                      <input
                        type="checkbox"
                        checked={selectedRewards.includes(reward.id)}
                        onChange={() => toggleRewardSelection(reward.id)}
                      />
                    )}
                  </td>
                  <td>
                    <div className="prm-referrer-info">
                      <span className="prm-referrer-name">
                        {reward.referrer?.name || reward.referrer?.merchant?.businessName || '-'}
                      </span>
                      <code className="prm-referrer-code">{reward.referrer?.referralCode}</code>
                    </div>
                  </td>
                  <td>
                    <span className={`prm-type-badge prm-type-${reward.referrer?.type?.toLowerCase()}`}>
                      {reward.referrer?.type}
                    </span>
                  </td>
                  <td>{reward.triggerEvent}</td>
                  <td className="prm-amount">{formatCurrency(reward.rewardAmount)}</td>
                  <td><StatusBadge status={reward.status} /></td>
                  <td>{formatDate(reward.eligibleAt)}</td>
                  <td>{formatDate(reward.createdAt)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {rewardsPagination.pages > 1 && (
        <div className="prm-pagination">
          <button
            className="prm-btn prm-btn-secondary"
            onClick={() => setRewardsPage(p => Math.max(1, p - 1))}
            disabled={rewardsPage === 1}
          >
            <ChevronLeft size={16} />
          </button>
          <span className="prm-pagination-info">
            Page {rewardsPage} of {rewardsPagination.pages}
          </span>
          <button
            className="prm-btn prm-btn-secondary"
            onClick={() => setRewardsPage(p => Math.min(rewardsPagination.pages, p + 1))}
            disabled={rewardsPage === rewardsPagination.pages}
          >
            <ChevronRight size={16} />
          </button>
        </div>
      )}
    </div>
  );

  // Render payouts tab — admin queue with side-panel detail
  const renderPayouts = () => (
    <div className="prm-payouts">
      <div className="prm-filters">
        <select
          className="prm-select"
          value={payoutStatus}
          onChange={e => { setPayoutStatus(e.target.value); setPayoutsPage(1); }}
        >
          <option value="all">All</option>
          <option value="PENDING">Pending</option>
          <option value="PROCESSING">Processing</option>
          <option value="PAID">Paid</option>
          <option value="REJECTED">Rejected</option>
        </select>
      </div>

      <div className="prm-table-container">
        <table className="prm-table">
          <thead>
            <tr>
              <th>Referrer</th>
              <th>Amount</th>
              <th>Bank</th>
              <th>Account</th>
              <th>Verified</th>
              <th>Status</th>
              <th>Requested</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {payouts.length === 0 ? (
              <tr><td colSpan={8} className="prm-empty">No payouts found</td></tr>
            ) : payouts.map(p => (
              <tr key={p.id}>
                <td>
                  <div className="prm-referrer-info">
                    <span className="prm-referrer-name">{p.referrer?.name || '—'}</span>
                    <span className="prm-referrer-email">{p.referrer?.email}</span>
                  </div>
                </td>
                <td className="prm-amount">{formatCurrency(p.amount)}</td>
                <td>{p.bankName}</td>
                <td>
                  <div className="prm-referrer-info">
                    <span className="prm-referrer-name">••{String(p.accountNumber || '').slice(-4)}</span>
                    <span className="prm-referrer-email">{p.accountName}</span>
                  </div>
                </td>
                <td>
                  {p.accountNameVerified ? (
                    <span className="prm-badge prm-badge-success">✓ {p.verificationProvider || 'Verified'}</span>
                  ) : (
                    <span className="prm-badge prm-badge-warning">Manual</span>
                  )}
                </td>
                <td><StatusBadge status={p.status} /></td>
                <td>{formatDate(p.createdAt)}</td>
                <td>
                  <button
                    className="prm-btn prm-btn-secondary"
                    onClick={() => {
                      setSelectedPayout(p);
                      setPayoutPaymentRef(p.paymentReference || '');
                      setPayoutAdminNotes(p.adminNotes || '');
                    }}
                  >
                    <Eye size={14} /> Open
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {payoutsPagination.pages > 1 && (
        <div className="prm-pagination">
          <button
            className="prm-btn prm-btn-secondary"
            onClick={() => setPayoutsPage(p => Math.max(1, p - 1))}
            disabled={payoutsPage === 1}
          ><ChevronLeft size={16} /></button>
          <span className="prm-pagination-info">Page {payoutsPage} of {payoutsPagination.pages}</span>
          <button
            className="prm-btn prm-btn-secondary"
            onClick={() => setPayoutsPage(p => Math.min(payoutsPagination.pages, p + 1))}
            disabled={payoutsPage === payoutsPagination.pages}
          ><ChevronRight size={16} /></button>
        </div>
      )}

      {/* Detail side panel */}
      {selectedPayout && (
        <div className="prm-detail-overlay" onClick={() => setSelectedPayout(null)}>
          <div className="prm-detail-sidebar" onClick={e => e.stopPropagation()}>
            <div className="prm-detail-header">
              <h3>Payout Request</h3>
              <button className="prm-icon-btn" onClick={() => setSelectedPayout(null)}>
                <XCircle size={20} />
              </button>
            </div>
            <div className="prm-detail-content">
              <div className="prm-detail-section">
                <h4>Amount</h4>
                <div className="prm-detail-row">
                  <span>Requested:</span>
                  <span style={{ fontSize: 18, fontWeight: 700 }}>{formatCurrency(selectedPayout.amount)}</span>
                </div>
                <div className="prm-detail-row">
                  <span>Status:</span>
                  <StatusBadge status={selectedPayout.status} />
                </div>
              </div>

              <div className="prm-detail-section">
                <h4>Referrer</h4>
                <div className="prm-detail-row"><span>Name:</span><span>{selectedPayout.referrer?.name}</span></div>
                <div className="prm-detail-row"><span>Email:</span><span>{selectedPayout.referrer?.email}</span></div>
                {selectedPayout.referrer?.phone && (
                  <div className="prm-detail-row"><span>Phone:</span><span>{selectedPayout.referrer.phone}</span></div>
                )}
                <div className="prm-detail-row"><span>Code:</span><code>{selectedPayout.referrer?.referralCode}</code></div>
              </div>

              <div className="prm-detail-section">
                <h4>Pay to</h4>
                <div className="prm-detail-row"><span>Bank:</span><span>{selectedPayout.bankName}</span></div>
                <div className="prm-detail-row"><span>Account #:</span><span>{selectedPayout.accountNumber}</span></div>
                <div className="prm-detail-row">
                  <span>Account name:</span>
                  <span>
                    {selectedPayout.accountName}
                    {selectedPayout.accountNameVerified && (
                      <span className="prm-badge prm-badge-success" style={{ marginLeft: 8 }}>
                        ✓ {selectedPayout.verificationProvider || 'Verified'}
                      </span>
                    )}
                  </span>
                </div>
                {selectedPayout.requestNote && (
                  <div className="prm-detail-row"><span>Note:</span><span>{selectedPayout.requestNote}</span></div>
                )}
              </div>

              {(selectedPayout.status === 'PENDING' || selectedPayout.status === 'PROCESSING') && (
                <div className="prm-detail-section">
                  <h4>Process Payment</h4>
                  <div className="prm-config-field">
                    <label>Payment reference (Paystack ref / bank ref)</label>
                    <input
                      type="text"
                      value={payoutPaymentRef}
                      onChange={e => setPayoutPaymentRef(e.target.value)}
                      placeholder="e.g. TRF_xxxxxxxxxx"
                    />
                  </div>
                  <div className="prm-config-field">
                    <label>Admin notes (optional, internal)</label>
                    <textarea
                      rows={2}
                      value={payoutAdminNotes}
                      onChange={e => setPayoutAdminNotes(e.target.value)}
                      placeholder="Anything we should remember about this payout"
                    />
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 12 }}>
                    {selectedPayout.status === 'PENDING' && (
                      <button
                        className="prm-btn prm-btn-secondary"
                        onClick={() => handlePayoutProcessing(selectedPayout.id)}
                        disabled={actionLoading}
                      >
                        <Clock size={14} /> Mark Processing
                      </button>
                    )}
                    <button
                      className="prm-btn prm-btn-success"
                      onClick={() => handlePayoutPaid(selectedPayout.id)}
                      disabled={actionLoading || !payoutPaymentRef.trim()}
                    >
                      <CheckCircle size={14} /> Mark Paid
                    </button>
                    <button
                      className="prm-btn prm-btn-danger"
                      onClick={() => handlePayoutReject(selectedPayout.id)}
                      disabled={actionLoading}
                    >
                      <XCircle size={14} /> Reject (refund wallet)
                    </button>
                  </div>
                </div>
              )}

              {selectedPayout.status === 'PAID' && selectedPayout.paymentReference && (
                <div className="prm-detail-section">
                  <h4>Settlement</h4>
                  <div className="prm-detail-row"><span>Reference:</span><code>{selectedPayout.paymentReference}</code></div>
                  <div className="prm-detail-row"><span>Paid:</span><span>{formatDate(selectedPayout.paidAt)}</span></div>
                </div>
              )}

              {selectedPayout.status === 'REJECTED' && (
                <div className="prm-detail-section">
                  <h4>Rejection</h4>
                  <div className="prm-detail-row"><span>Reason:</span><span>{selectedPayout.rejectionReason}</span></div>
                  <div className="prm-detail-row"><span>Rejected:</span><span>{formatDate(selectedPayout.rejectedAt)}</span></div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // Normalize whatever shape the config is currently in. Returns the
  // nested { tier: { base, performance } } form so the UI is uniform.
  const normalizeRewards = (rewards) => {
    const out = {};
    Object.entries(rewards || {}).forEach(([tier, value]) => {
      if (value && typeof value === 'object') {
        out[tier] = { base: Number(value.base) || 0, performance: Number(value.performance) || 0 };
      } else {
        // Legacy flat number — treat as base, performance = same value
        const n = Number(value) || 0;
        out[tier] = { base: n, performance: n };
      }
    });
    return out;
  };

  // Update a single rate (base or performance) for one tier within one
  // referrer-type table.
  const updateRate = (type, tier, kind, value) => {
    const field = type === 'merchant' ? 'merchantRewardsByTier' : 'marketerRewardsByTier';
    const next = normalizeRewards(config[field]);
    next[tier] = { ...(next[tier] || { base: 0, performance: 0 }), [kind]: parseInt(value) || 0 };
    setConfig({ ...config, [field]: next });
  };

  // Render config tab
  const renderConfig = () => {
    if (!config) return <div className="prm-loading">Loading config...</div>;

    const merchantRewards = normalizeRewards(config.merchantRewardsByTier);
    const marketerRewards = normalizeRewards(config.marketerRewardsByTier);

    // All tiers including FREE — under the new spec, FREE acquisition pays too
    const availableTiers = tiers.length > 0 ? tiers : [
      { id: 'FREE', name: 'Free' },
      { id: 'PREMIUM', name: 'Premium' },
      { id: 'PRO', name: 'Pro' },
      { id: 'PRO PLUS', name: 'Pro Plus' },
      { id: 'ENTERPRISE', name: 'Enterprise' }
    ];

    const renderRateGrid = (type, rewards) => (
      <div className="prm-config-grid prm-config-grid-dynamic">
        {availableTiers.map(tier => {
          const rate = rewards[tier.id] || { base: 0, performance: 0 };
          return (
            <div
              key={`${type}-${tier.id}`}
              className={`prm-config-field ${tier.isActive === false ? 'prm-tier-inactive' : ''}`}
              title={tier.isActive === false ? 'This tier is inactive' : ''}
            >
              <label>{tier.name}</label>
              <div style={{ display: 'flex', gap: '8px' }}>
                <input
                  type="number"
                  value={rate.base}
                  onChange={e => updateRate(type, tier.id, 'base', e.target.value)}
                  placeholder="Base"
                  title="Base rate"
                  min="0"
                />
                <input
                  type="number"
                  value={rate.performance}
                  onChange={e => updateRate(type, tier.id, 'performance', e.target.value)}
                  placeholder="Performance"
                  title="Performance rate (after threshold)"
                  min="0"
                />
              </div>
            </div>
          );
        })}
      </div>
    );

    return (
      <div className="prm-config">
        {/* How It Works Info Box */}
        <div className="prm-config-section prm-config-info">
          <h3>How Referral Rewards Work</h3>
          <div className="prm-info-box">
            <p><strong>One-time reward per acquired merchant, locked to the tier they signed up at, paid at email verification.</strong></p>
            <ul>
              <li><strong>Free signup verified</strong> → free-tier rate</li>
              <li><strong>Paid signup verified</strong> (Premium / Pro / Pro+ / Enterprise) → that tier's rate</li>
              <li><strong>Upgrades after signup pay nothing</strong> — those are platform marketing, not the referrer's effort</li>
              <li>Performance rates kick in once a referrer's lifetime verified-referral count reaches the threshold below</li>
            </ul>
            <p className="prm-info-example">
              Example: marketer brings a Free user who verifies → ₦200. Marketer brings a Pro user who verifies → ₦350. The Free user later upgrades to Pro → ₦0 (no additional reward).
            </p>
          </div>
        </div>

        {/* Performance threshold */}
        <div className="prm-config-section">
          <h3>Performance Tier Threshold</h3>
          <p className="prm-config-hint">
            Lifetime verified referrals required before a referrer is moved to performance rates.
          </p>
          <div className="prm-config-grid">
            <div className="prm-config-field">
              <label>Verified referrals required</label>
              <input
                type="number"
                value={config.performanceThreshold ?? 200}
                onChange={e => setConfig({ ...config, performanceThreshold: parseInt(e.target.value) || 0 })}
                min="0"
              />
            </div>
          </div>
        </div>

        {/* Default minimum payout */}
        <div className="prm-config-section">
          <h3>Default Minimum Payout (₦)</h3>
          <p className="prm-config-hint">
            Applied to new referrers at signup. Existing referrers keep their own value (admin can override per-row from the referrer detail view in the future).
          </p>
          <div className="prm-config-grid">
            <div className="prm-config-field">
              <label>Minimum payout amount</label>
              <input
                type="number"
                value={config.defaultMinimumPayout ?? 2000}
                onChange={e => setConfig({ ...config, defaultMinimumPayout: parseInt(e.target.value) || 0 })}
                min="0"
                step="100"
              />
            </div>
          </div>
        </div>

        {/* Merchant Rewards Section */}
        <div className="prm-config-section">
          <h3>Merchant Referrer Rewards (₦)</h3>
          <p className="prm-config-hint">Left column = base rate. Right column = performance rate (after threshold).</p>
          {renderRateGrid('merchant', merchantRewards)}
        </div>

        {/* Marketer Rewards Section */}
        <div className="prm-config-section">
          <h3>Marketer Referrer Rewards (₦)</h3>
          <p className="prm-config-hint">Left column = base rate. Right column = performance rate (after threshold).</p>
          {renderRateGrid('marketer', marketerRewards)}
        </div>

        <div className="prm-config-section">
          <h3>Hold Periods (Days)</h3>
          <p className="prm-config-hint">Time to wait before rewards become eligible for payout</p>
          <div className="prm-config-grid">
            <div className="prm-config-field">
              <label>Signup Hold</label>
              <input
                type="number"
                value={config.signupHoldDays || 7}
                onChange={e => setConfig({...config, signupHoldDays: parseInt(e.target.value)})}
                min="0"
              />
            </div>
            <div className="prm-config-field">
              <label>Free Signup Hold</label>
              <input
                type="number"
                value={config.verificationHoldDays || 3}
                onChange={e => setConfig({...config, verificationHoldDays: parseInt(e.target.value)})}
                min="0"
                title="Hold period for rewards from Free-tier signups"
              />
            </div>
            <div className="prm-config-field">
              <label>Paid Signup Hold</label>
              <input
                type="number"
                value={config.upgradeHoldDays || 14}
                onChange={e => setConfig({...config, upgradeHoldDays: parseInt(e.target.value)})}
                min="0"
                title="Hold period for rewards from paid-tier signups (Premium / Pro / Pro+)"
              />
            </div>
          </div>
        </div>

        <div className="prm-config-section">
          <h3>Rate Limits</h3>
          <p className="prm-config-hint">Prevent abuse by limiting referral rate</p>
          <div className="prm-config-grid">
            <div className="prm-config-field">
              <label>Max Referrals/Day</label>
              <input
                type="number"
                value={config.maxReferralsPerDay || 10}
                onChange={e => setConfig({...config, maxReferralsPerDay: parseInt(e.target.value)})}
                min="1"
              />
            </div>
            <div className="prm-config-field">
              <label>Max Referrals/Week</label>
              <input
                type="number"
                value={config.maxReferralsPerWeek || 50}
                onChange={e => setConfig({...config, maxReferralsPerWeek: parseInt(e.target.value)})}
                min="1"
              />
            </div>
          </div>
        </div>

        <div className="prm-config-section">
          <h3>Program Settings</h3>
          <div className="prm-config-toggles">
            <label className="prm-toggle">
              <input
                type="checkbox"
                checked={config.isActive ?? true}
                onChange={e => setConfig({...config, isActive: e.target.checked})}
              />
              <span>Program Active</span>
            </label>
            <label className="prm-toggle">
              <input
                type="checkbox"
                checked={config.allowMerchantReferrers ?? true}
                onChange={e => setConfig({...config, allowMerchantReferrers: e.target.checked})}
              />
              <span>Allow Merchant Referrers</span>
            </label>
            <label className="prm-toggle">
              <input
                type="checkbox"
                checked={config.allowMarketerReferrers ?? true}
                onChange={e => setConfig({...config, allowMarketerReferrers: e.target.checked})}
              />
              <span>Allow Marketer Referrers</span>
            </label>
            <label className="prm-toggle">
              <input
                type="checkbox"
                checked={config.autoApproveRewards ?? false}
                onChange={e => setConfig({...config, autoApproveRewards: e.target.checked})}
              />
              <span>Auto-Approve Rewards</span>
            </label>
            <label className="prm-toggle">
              <input
                type="checkbox"
                checked={config.blockDisposableEmails ?? true}
                onChange={e => setConfig({...config, blockDisposableEmails: e.target.checked})}
              />
              <span>Block Disposable Emails</span>
            </label>
          </div>
        </div>

        <div className="prm-config-actions">
          <button
            className="prm-btn prm-btn-primary"
            onClick={handleSaveConfig}
            disabled={actionLoading}
          >
            {actionLoading ? 'Saving...' : 'Save Configuration'}
          </button>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="prm-container">
        <div className="prm-loading">
          <RefreshCw size={24} className="prm-spinner" />
          <span>Loading referral data...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="prm-container">
      {/* Header */}
      <div className="prm-header">
        <div className="prm-header-left">
          <h1 className="prm-title">Platform Referrals</h1>
          <p className="prm-subtitle">Manage referrers, track referrals, and approve rewards</p>
        </div>
        <button
          className="prm-btn prm-btn-secondary"
          onClick={() => {
            fetchOverview();
            if (activeTab === 'referrers') fetchReferrers(referrersPage);
            if (activeTab === 'rewards') fetchRewards(rewardsPage);
          }}
        >
          <RefreshCw size={16} />
          Refresh
        </button>
      </div>

      {/* Tabs */}
      <div className="prm-tabs">
        <button
          className={`prm-tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          <TrendingUp size={16} />
          Overview
        </button>
        <button
          className={`prm-tab ${activeTab === 'referrers' ? 'active' : ''}`}
          onClick={() => setActiveTab('referrers')}
        >
          <Users size={16} />
          Referrers
        </button>
        <button
          className={`prm-tab ${activeTab === 'rewards' ? 'active' : ''}`}
          onClick={() => setActiveTab('rewards')}
        >
          <Gift size={16} />
          Rewards
        </button>
        <button
          className={`prm-tab ${activeTab === 'payouts' ? 'active' : ''}`}
          onClick={() => setActiveTab('payouts')}
        >
          <DollarSign size={16} />
          Payouts
        </button>
        <button
          className={`prm-tab ${activeTab === 'config' ? 'active' : ''}`}
          onClick={() => setActiveTab('config')}
        >
          <Settings size={16} />
          Config
        </button>
      </div>

      {/* Tab Content */}
      <div className="prm-content">
        {activeTab === 'overview' && renderOverview()}
        {activeTab === 'referrers' && renderReferrers()}
        {activeTab === 'rewards' && renderRewards()}
        {activeTab === 'payouts' && renderPayouts()}
        {activeTab === 'config' && renderConfig()}
      </div>

      {/* Toast */}
      {toast && <Toast {...toast} onClose={() => setToast(null)} />}

      {/* Modals */}
      <ReasonModal
        isOpen={reasonModal.isOpen}
        title={reasonModal.title}
        onSubmit={reasonModal.onSubmit}
        onCancel={() => setReasonModal({ isOpen: false })}
        placeholder={reasonModal.placeholder}
      />
    </div>
  );
}
